import { useQueryClient } from '@tanstack/react-query';
import { Check, CircleAlert, Copy, KeyRound, RefreshCw, Undo2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ConnectionMode, ServerConnectionDTO } from '@harness/shared';
import { PageHeader, Spinner, statusLabel, Switch } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { clockTime, relTime } from '../../lib/format';
import { qk } from '../../lib/keys';
import { useAudit, useCheckpoints, useServer, type ProjectDetail } from '../../lib/queries';
import { toast } from '../../lib/store';

export const MODES: { value: ConnectionMode; label: string; desc: string }[] = [
  { value: 'daytona', label: 'Daytona workspace', desc: 'A persistent cloud sandbox for code, shell and Git' },
  { value: 'runner', label: 'Harness Runner', desc: 'Outbound WebSocket from a small daemon · recommended for servers behind NAT' },
  { value: 'ssh', label: 'SSH', desc: 'Per-project key pair · add the public key to authorized_keys' },
  { value: 'local', label: 'Local host', desc: 'Harness and project on one machine, sandboxed' },
];

const CAPS: { key: keyof ServerConnectionDTO['capabilities']; label: string; desc: string; locked?: boolean }[] = [
  { key: 'fs', label: 'File system', desc: 'List, read, write, patch, search', locked: true },
  { key: 'shell', label: 'Shell', desc: 'Commands with timeout, background jobs' },
  { key: 'git', label: 'Git', desc: 'Status, diff, commit, branch · push gated' },
  { key: 'port', label: 'Port preview', desc: 'Proxy a dev server port into the UI' },
  { key: 'docker', label: 'Container exec', desc: 'Run inside the project’s Docker Compose services' },
];

const MODE_NAME: Record<ServerConnectionDTO['mode'], string> = { daytona: 'Daytona', runner: 'Runner', ssh: 'SSH', local: 'Local host' };

/** Teal banner when the server is online; a cream warning tile otherwise. */
function StatusBanner({ conn }: { conn: ServerConnectionDTO | null | undefined }) {
  if (!conn)
    return (
      <div className="tile tile-cream banner-tile">
        <CircleAlert className="icon" style={{ fontSize: 20 }} />
        <div>
          <div className="bt">No server connected</div>
          <div className="bs">Pick a connection mode below. Agents can chat without one, but file, shell and git tools stay off.</div>
        </div>
      </div>
    );
  if (conn.status === 'online')
    return (
      <div className="success-banner">
        <span className="success-icon">
          <Check className="icon" />
        </span>
        <div>
          <div className="title-900" style={{ fontSize: 20, lineHeight: 1.2 }}>
            {MODE_NAME[conn.mode]} connected to {conn.host || conn.workingDir}
          </div>
          <div className="muted">
            {conn.mode === 'runner'
              ? 'Outbound WebSocket. Commands run as a low-privilege user, confined to the working directory.'
              : conn.mode === 'daytona'
                ? `Cloud workspace at ${conn.workingDir}. The filesystem persists when the sandbox stops.`
                : conn.mode === 'ssh'
                ? `SSH with a per-project key as ${conn.user}. Commands are confined to ${conn.workingDir}.`
                : `Same host, sandboxed to ${conn.workingDir} with a clean environment.`}
          </div>
        </div>
      </div>
    );
  return (
    <div className="tile tile-cream banner-tile">
      <CircleAlert className="icon" style={{ fontSize: 20 }} />
      <div>
        <div className="bt">
          {MODE_NAME[conn.mode]} {conn.status === 'unreachable' ? 'unreachable' : 'offline'}
          {conn.lastSeenAt ? ` · last seen ${relTime(conn.lastSeenAt).toLowerCase()}` : ''}
        </div>
        <div className="bs">Tool use is blocked until the connection is back. Run a health check, or check the settings below.</div>
      </div>
    </div>
  );
}

export function MfaRequired() {
  return (
    <div className="status-line" style={{ borderColor: 'var(--color-accent-300)', background: 'var(--color-accent-100)', color: 'var(--color-accent-800)' }}>
      <span style={{ flex: 1 }}>Two-step verification must be on before this harness can hold server access or API keys.</span>
      <Link className="btn btn-secondary" to="/settings/account">
        Set up two-step verification
      </Link>
    </div>
  );
}

export default function ServerPage({ project }: { project: ProjectDetail }) {
  const qc = useQueryClient();
  const server = useServer(project.id);
  const audit = useAudit(project.id, 60);
  const checkpoints = useCheckpoints(project.id);
  const conn = server.data?.connection ?? null;
  const [mode, setMode] = useState<ConnectionMode>('ssh');
  const [host, setHost] = useState('');
  const [port, setPort] = useState('22');
  const [workingDir, setWorkingDir] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [mfa, setMfa] = useState(false);
  const [pair, setPair] = useState<{ command: string; expiresAt: string } | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  useEffect(() => {
    if (!server.data) return;
    const c = server.data.connection;
    setMode(c?.mode ?? (server.data.daytonaAvailable ? 'daytona' : 'ssh'));
    setHost(c ? (c.mode === 'ssh' ? `${c.user}@${c.host}` : c.host) : '');
    setPort(String(c?.port ?? 22));
    setWorkingDir(c?.workingDir ?? '');
  }, [server.data]);

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: qk.server(project.id) });
    await qc.invalidateQueries({ queryKey: qk.projects });
    await qc.invalidateQueries({ queryKey: qk.audit(project.id) });
  };

  const put = async (body: Record<string, unknown>) => {
    setMfa(false);
    try {
      await api.put(`/api/projects/${project.id}/server`, body);
      await refresh();
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
      return false;
    }
  };

  const save = async () => {
    setBusy(true);
    const ok = await put({ mode, host: mode === 'ssh' ? host : undefined, port: Number(port) || 22, workingDir: workingDir || undefined });
    setBusy(false);
    if (ok) toast(mode === 'runner' ? 'Saved · pair a runner to bring it online' : 'Connection saved · health check run');
  };

  const check = async () => {
    setChecking(true);
    try {
      const r = await api.post<{ lastError: string | null }>(`/api/projects/${project.id}/server/check`);
      await refresh();
      if (r.lastError) toast(r.lastError, 'error');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setChecking(false);
    }
  };

  const issueToken = async () => {
    setMfa(false);
    try {
      setPair(await api.post(`/api/projects/${project.id}/server/pair`));
      await refresh();
    } catch (e) {
      if (e instanceof ApiError && e.code === 'mfa_required') setMfa(true);
      else toast(errorText(e), 'error');
    }
  };

  const rotate = async () => {
    try {
      await api.post(`/api/projects/${project.id}/server/rotate-key`);
      await refresh();
      toast('New key pair generated · replace the old public key on the server');
    } catch (e) {
      toast(errorText(e), 'error');
    }
  };

  const restore = async (id: string) => {
    setRestoring(id);
    try {
      await api.post(`/api/checkpoints/${id}/restore`);
      await qc.invalidateQueries({ queryKey: qk.checkpoints(project.id) });
      await qc.invalidateQueries({ queryKey: qk.audit(project.id) });
      toast(`Restored ${id} on ${conn?.host || 'the server'}`);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setRestoring(null);
    }
  };

  const copy = (s: string) => void navigator.clipboard.writeText(s).then(() => toast('Copied'));

  if (server.isLoading)
    return (
      <div className="settings-content">
        <Spinner />
      </div>
    );

  return (
    <div className="settings-content" style={{ maxWidth: 1100 }}>
      <PageHeader title="Server connection" sub={`Where ${project.name}'s code lives and runs.`} />

      <StatusBanner conn={conn} />

      <div className="stat-tiles">
        <div className="tile tile-mint">
          <div className="t">Status</div>
          <div className="v" style={{ color: conn?.status === 'online' ? 'var(--tile-mint-ink)' : conn?.status === 'unreachable' ? 'var(--color-accent-700)' : 'var(--color-neutral-700)' }}>
            {conn ? statusLabel(conn.status) : 'Not connected'}
          </div>
        </div>
        <div className="tile tile-lavender">
          <div className="t">Host</div>
          <div className="v ellipsis" title={conn?.host}>{conn?.host || '—'}</div>
        </div>
        <div className="tile tile-blue">
          <div className="t">Last health check</div>
          <div className="v">{checking ? 'checking…' : conn?.lastCheckAt ? relTime(conn.lastCheckAt).replace('Now', 'just now') : '—'}</div>
        </div>
        <div className="tile tile-cream">
          <div className="t">Runs as</div>
          <div className="v ellipsis">{conn ? (conn.mode === 'ssh' ? conn.user : conn.mode === 'local' ? 'harness' : conn.mode === 'daytona' ? 'daytona' : 'runner') : '—'}</div>
          <div className="s ellipsis">{conn?.sandbox ?? 'low-privilege user'}</div>
        </div>
      </div>
      {server.data?.lastError && conn?.status !== 'online' && <div className="err">Last error: {server.data.lastError}</div>}
      {mfa && <MfaRequired />}

      <div className="card card-pad stack-sm">
        <div className="section-title">Connection mode</div>
        <div className="ruled-grid mode-grid">
          {MODES.filter((m) => server.data?.daytonaAvailable && !conn ? m.value === 'daytona' : conn?.mode === 'daytona' ? m.value === 'daytona' : m.value !== 'daytona').map((m) => (
            <button key={m.value} className="pick-card" aria-pressed={mode === m.value} onClick={() => setMode(m.value)}>
              <span className="t">
                {m.label}
                {conn?.mode === m.value ? ' · current' : ''}
              </span>
              <span className="d">{m.desc}</span>
            </button>
          ))}
        </div>

        <div className="stack-sm" style={{ marginTop: 6, maxWidth: 720 }}>
          {mode === 'daytona' && (
            <div className="hint">Creates a persistent Daytona sandbox in your Daytona account. Its files survive auto-stop; compute and retained disk may be billed. The API key stays on the harness server.</div>
          )}
          {mode === 'ssh' && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 100px', gap: 12 }}>
                <div className="field">
                  <label htmlFor="s-host">Host</label>
                  <input id="s-host" className="input mono" placeholder="deploy@10.0.4.21" value={host} onChange={(e) => setHost(e.target.value)} />
                </div>
                <div className="field">
                  <label htmlFor="s-port">Port</label>
                  <input id="s-port" className="input" inputMode="numeric" value={port} onChange={(e) => setPort(e.target.value.replace(/\D/g, ''))} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="s-wd">Working directory</label>
                <input id="s-wd" className="input mono" placeholder={`/srv/${project.slug}`} value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} />
              </div>
              {conn?.mode === 'ssh' && conn.publicKey && (
                <div className="field">
                  <span className="label">Add this key to ~/.ssh/authorized_keys on the server</span>
                  <pre className="code-light">{conn.publicKey}</pre>
                  <div className="row" style={{ marginTop: 8 }}>
                    <button className="btn btn-secondary" onClick={() => copy(conn.publicKey!)}>
                      <Copy className="icon" />
                      Copy key
                    </button>
                    <button className="btn btn-secondary" onClick={rotate}>
                      <KeyRound className="icon" />
                      Rotate key
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
          {mode === 'local' && (
            <div className="field">
              <label htmlFor="s-root">Project root on this host</label>
              <input id="s-root" className="input mono" placeholder={`${server.data?.localRoot}/${project.slug}`} value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} />
              <div className="hint">Must be under {server.data?.localRoot}. Commands run with a clean environment — no harness secrets.</div>
            </div>
          )}
          {mode === 'runner' && (
            <div className="field">
              <label htmlFor="s-rwd">Working directory on the server</label>
              <input id="s-rwd" className="input mono" placeholder={`/srv/${project.slug}`} value={workingDir} onChange={(e) => setWorkingDir(e.target.value)} />
              <div className="hint">Issue a pairing token below and run the command on the server. The runner confines every file and command to this directory.</div>
            </div>
          )}
          <div className="row" style={{ marginTop: 4 }}>
            <button className="btn btn-primary" onClick={save} disabled={busy}>
              {busy && <Spinner />}
              {conn ? (conn.mode === mode ? 'Save connection' : `Switch to ${MODES.find((m) => m.value === mode)!.label}`) : 'Connect'}
            </button>
            {mode === 'runner' && (
              <button className="btn btn-secondary" onClick={issueToken}>
                <KeyRound className="icon" />
                Issue new pairing token
              </button>
            )}
            {conn && (
              <button className="btn btn-secondary" onClick={check} disabled={checking}>
                {checking ? <Spinner /> : <RefreshCw className="icon" />}
                Run health check
              </button>
            )}
          </div>
          {pair && (
            <>
              <pre className="code-dark">{pair.command}</pre>
              <div className="hint">One-time token · expires {new Date(pair.expiresAt).toLocaleTimeString()}</div>
            </>
          )}
        </div>
      </div>

      <div className="cols-2">
        <div className="card card-pad">
          <div className="section-title">Capabilities</div>
          {CAPS.map((c) => (
            <div key={c.key} className="cap-row">
              <Switch
                on={conn?.capabilities[c.key] ?? c.key === 'fs'}
                disabled={c.locked || !conn}
                label={c.label}
                onChange={(v) => conn && void put({ mode: conn.mode, capabilities: { ...conn.capabilities, [c.key]: v } })}
              />
              <div>
                <div className="tool-label">{c.label}</div>
                <div className="muted" style={{ fontSize: 12 }}>
                  {c.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="card card-pad">
          <div className="section-title">Checkpoints</div>
          {(checkpoints.data ?? []).length === 0 && <div className="empty">No checkpoints yet. One is taken before each agent turn that writes files.</div>}
          {(checkpoints.data ?? []).map((c) => (
            <div key={c.id} className="row-line" style={{ gridTemplateColumns: '1fr auto' }}>
              <div style={{ minWidth: 0 }}>
                <div className="ellipsis tool-label">
                  {c.label}
                </div>
                <div className="muted mono ellipsis" style={{ fontSize: 11 }}>
                  {c.id} · {c.files} file{c.files === 1 ? '' : 's'} · {relTime(c.createdAt)}
                </div>
              </div>
              <button className="btn btn-secondary" disabled={!!c.restoredAt || restoring === c.id || conn?.status !== 'online'} onClick={() => restore(c.id)}>
                {restoring === c.id ? <Spinner /> : <Undo2 className="icon" />}
                {c.restoredAt ? 'Restored' : 'Undo to here'}
              </button>
            </div>
          ))}
        </div>
      </div>

      <div className="card audit-card">
        <div className="section-title" style={{ padding: '0 12px 4px' }}>
          Audit trail
        </div>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 110 }}>Time</th>
              <th style={{ width: 110 }}>Actor</th>
              <th style={{ width: 120 }}>Action</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {(audit.data ?? []).map((a) => (
              <tr key={a.id}>
                <td className="mono" style={{ fontSize: 12 }} title={new Date(a.createdAt).toLocaleString()}>
                  {clockTime(a.createdAt)}
                </td>
                <td style={{ fontWeight: 700 }}>{a.actor}</td>
                <td>
                  <span className="tag tag-neutral">{a.action}</span>
                </td>
                <td className="mono" style={{ fontSize: 12, wordBreak: 'break-word' }}>
                  {a.detail}
                </td>
              </tr>
            ))}
            {audit.data?.length === 0 && (
              <tr>
                <td colSpan={4} className="muted">
                  Nothing recorded yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
